import { useAtom } from 'jotai';
import { atom } from 'jotai';
import { useCallback } from 'react';
import { ExternalLink } from 'lucide-react';

import {
    Drawer,
    DrawerContent,
    DrawerDescription,
    DrawerHeader,
    DrawerTitle,
} from '@rocco/components/drawer';
import {
    PhotoDocumentViewerModal,
    type ViewerItem,
} from '@rocco/components/photo-document-viewer';
import { usePhotoDocumentViewer } from '@rocco/hooks/use-photo-document-viewer';
import { Comments } from '../../../../build-hub-comment/components/Comments';
import type { DetailedPinWithChecked } from '~/modules/design-board/component/all-pins/AllPins';
import { Tile } from '@rocco/ui/tile-shared/components/Tile';
import { isArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { VotingControl } from './voting-control';
import { useBuildHubBoardItemVoting } from '../../../hooks/use-build-hub-board-item-voting';
import { ReactionTypeEnum } from '../../../../../../generated/graphql';
import { Button } from '@rocco/components/button';
import { cn } from '@rocco/utils/cn';

interface ItemDetailDrawerAtomType {
    pin: DetailedPinWithChecked;
}

/**
 * Atom to store the currently selected item ID for the detail drawer
 * When null, the drawer is closed. When a number, the drawer is open for that item.
 */
const moodBoardItemDetailDrawerAtom = atom<ItemDetailDrawerAtomType | null>(
    null
);

/**
 * UI component for the item detail drawer
 * Renders the drawer with comments when an item is selected
 */
export const MoodboardItemDetailDrawer = () => {
    const [item, setItem] = useAtom(moodBoardItemDetailDrawerAtom);

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            setItem(null);
        }
    };

    if (!item) return null;

    const pin = item.pin;
    const itemId = pin?.ID;

    return (
        <Drawer
            open={!!itemId}
            onOpenChange={handleOpenChange}
            direction="right"
        >
            <DrawerContent
                className="data-[vaul-drawer-direction=right]:sm:max-w-[33.25rem]"
                variant="default"
                overlayVariant="dark"
                noHandle={true}
                scrollable={true}
                closeButtonClassName="md:top-4 md:right-4"
            >
                <DrawerHeader>
                    <DrawerTitle aria-label="Comments" />
                    {/* Suppress missing description console warning */}
                    <DrawerDescription className="hidden" aria-hidden />
                </DrawerHeader>
                {itemId && <ItemDetailDrawerContent pin={pin} />}
            </DrawerContent>
        </Drawer>
    );
};

const ItemDetailDrawerContent = ({ pin }: { pin: DetailedPinWithChecked }) => {
    const isArticleTile = isArticleTileViewModel(pin.TileData);
    const { isOpen, items, openViewer, closeViewer } = usePhotoDocumentViewer();

    // Type assertion to include ExternalUrlContent which is BuildHub-specific
    const Target = pin.Target as
        | DetailedPinWithChecked['Target']
        | {
              __typename: 'ExternalUrlContent';
              SourceUrl?: string | null;
              Title?: string | null;
          };
    const isExternalUrl = Target.__typename === 'ExternalUrlContent';

    const {
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
    } = useBuildHubBoardItemVoting({
        targetId: pin.ID,
    });

    const handleVoteUp = useCallback(() => {
        if (isVotedUp) {
            handleRevokeVote(ReactionTypeEnum.UpVote);
        } else {
            handleUpVote();
        }
    }, [isVotedUp, handleRevokeVote, handleUpVote]);

    const handleVoteDown = useCallback(() => {
        if (isVotedDown) {
            handleRevokeVote(ReactionTypeEnum.DownVote);
        } else {
            handleDownVote();
        }
    }, [isVotedDown, handleRevokeVote, handleDownVote]);

    /**
     * Opens the external URL in a new tab
     */
    const handleExternalLinkClick = useCallback(
        (e?: React.MouseEvent) => {
            if (isExternalUrl && Target.__typename === 'ExternalUrlContent') {
                const url = Target.SourceUrl;
                if (url) {
                    e?.preventDefault();
                    e?.stopPropagation();
                    window.open(url, '_blank', 'noopener,noreferrer');
                }
            }
        },
        [isExternalUrl, Target]
    );

    const handleExpandButtonClick = useCallback(() => {
        // Convert pin images to viewer items based on tile type
        const viewerItems: ViewerItem[] = [];

        if (Target.__typename === 'MemberUpload') {
            const filename = Target.FileName;
            const isImage =
                filename &&
                (filename.endsWith('.jpg') ||
                    filename.endsWith('.jpeg') ||
                    filename.endsWith('.png') ||
                    filename.endsWith('.gif') ||
                    filename.endsWith('.bmp') ||
                    filename.endsWith('.tiff') ||
                    filename.endsWith('.ico') ||
                    filename.endsWith('.webp'));
            const isPDF = filename && filename.endsWith('.pdf');
            const isVideo =
                filename &&
                (filename.endsWith('.mp4') ||
                    filename.endsWith('.mov') ||
                    filename.endsWith('.avi') ||
                    filename.endsWith('.mkv'));
            const isUnsupportedFile = !isImage && !isPDF && !isVideo;

            if (isImage) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'image',
                });
            } else if (isVideo) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'video',
                });
            } else if (isPDF) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'document',
                });
            } else if (isUnsupportedFile) {
                viewerItems.push({
                    id: `contentViewer_${pin.ID}`,
                    url: Target.Link,
                    title: Target.Title,
                    type: 'file',
                });
            }
        }

        if (Target.__typename === 'LibraryImage') {
            viewerItems.push({
                id: `libraryImageViewer_${pin.ID}`,
                url: Target.LibraryImageFile?.PhotoTile || pin.PhotoTile,
                title: Target.LibraryItem?.Title || pin.Title,
                type: 'image',
            });
        }

        if (viewerItems.length > 0) {
            openViewer(viewerItems);
        }
    }, [Target, pin.ID, pin.PhotoTile, pin.Title, openViewer]);

    /**
     * Custom action button for external URLs
     * Shows external link icon instead of expand icon
     */
    const externalLinkActionButton = isExternalUrl ? (
        <div className="absolute right-4 top-3.5 flex gap-1 transition-opacity duration-300">
            <Button
                type="button"
                size="square42"
                color="secondary70"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleExternalLinkClick();
                }}
                aria-label="Open external link in new tab"
            >
                <ExternalLink className={cn('size-4')} />
            </Button>
        </div>
    ) : undefined;

    return (
        <div className="flex h-full flex-col pt-12">
            {/* Pin details */}
            <div className="relative flex-shrink-0 px-4 pb-4">
                <Tile
                    tile={{
                        aspectRatio: 'default',
                        model: {
                            ...pin.TileData,
                            badge: null,
                            ...(isArticleTile && {
                                variant: 'default',
                            }),
                        },
                        hideFollowButton: true,
                        // For external URLs: make tile clickable to open link
                        // For other types: show expand button for viewer
                        disableLink: true,
                        onClick: isExternalUrl
                            ? handleExternalLinkClick
                            : undefined,
                        onExpandButtonClick: isExternalUrl
                            ? undefined
                            : handleExpandButtonClick,
                        className: cn(
                            '[&_.rocco-tile-description-block]:max-w-[75%]',
                            isExternalUrl && 'cursor-pointer'
                        ),
                    }}
                    inlineActionButtons={externalLinkActionButton}
                />
                <div className="absolute bottom-4 right-0 px-4">
                    <VotingControl
                        hasVotedUp={isVotedUp}
                        hasVotedDown={isVotedDown}
                        upVoteCollaborators={upVoteCollaborators}
                        downVoteCollaborators={downVoteCollaborators}
                        onVoteUp={handleVoteUp}
                        onVoteDown={handleVoteDown}
                    />
                </div>
            </div>
            <div className="mx-4 mb-4 h-px bg-[#E4E5E4]" />
            {/* Comment section */}
            <div className="mx-4 min-h-0 flex-1">
                <Comments itemId={pin.ID} />
            </div>

            {/* Photo/Document Viewer Modal */}
            <PhotoDocumentViewerModal
                open={isOpen}
                onOpenChange={closeViewer}
                items={items}
                title="Pin Gallery"
            />
        </div>
    );
};

/**
 * Custom hook to manage the item detail drawer state
 * Provides functions to open and close the drawer, and access to the current item ID
 */
export const useMoodboardItemDetailDrawer = () => {
    const [item, setItem] = useAtom(moodBoardItemDetailDrawerAtom);

    const openDialog = (id: DetailedPinWithChecked) => {
        setItem({ pin: id });
    };

    const closeDialog = () => {
        setItem(null);
    };

    return {
        item: item?.pin,
        openDialog,
        closeDialog,
        isOpen: !!item?.pin,
    };
};
